import firebaseService from '../Services/firebaseService';
import { isRelayOnlyRequested } from './relayOnly';

const GOOGLE_PUBLIC_STUN_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
];

const iceServers = [
    { urls: "stun:myturn.fileplease.com:3478" },
    ...GOOGLE_PUBLIC_STUN_SERVERS,
];

const DEFAULT_ICE_TRANSPORT_POLICY: RTCIceTransportPolicy = 'all';

// Local provider comparison; production always uses Firebase-issued credentials.
const localIceServers: RTCIceServer[] | null =
    process.env.NODE_ENV === 'development' && process.env.REACT_APP_LOCAL_ICE_SERVERS
        ? JSON.parse(process.env.REACT_APP_LOCAL_ICE_SERVERS)
        : null;

export class WebRTCConnectionFactory {
    static async configureTurn(peer: RTCPeerConnection): Promise<void> {
        const servers = localIceServers ?? [
            ...iceServers,
            ...(await firebaseService.getTurnCredentials()).iceServers,
        ];
        if (peer.signalingState === 'closed') {
            throw new Error('Connection was closed while obtaining credentials.');
        }
        const config = peer.getConfiguration();
        peer.setConfiguration({
            ...config,
            iceServers: config.iceTransportPolicy === 'relay'
                ? servers.filter(server => {
                    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
                    return urls.some(url => /^turns?:/.test(url));
                })
                : servers,
        });
    }

    private static createConfig(
        overrides?: Partial<RTCConfiguration>,
    ): RTCConfiguration {
        return {
            iceServers: localIceServers ?? iceServers,
            iceTransportPolicy: DEFAULT_ICE_TRANSPORT_POLICY,
            ...overrides,
            ...(isRelayOnlyRequested() ? { iceTransportPolicy: 'relay' as const } : {}),
        };
    }

    static createPeerConnection(
        overrides?: Partial<RTCConfiguration>,
    ): RTCPeerConnection {
        return new RTCPeerConnection(this.createConfig(overrides));
    }

    static createRelayOnlyPeerConnection(): RTCPeerConnection {
        return this.createPeerConnection({
            iceServers: [],
            iceTransportPolicy: 'relay',
        });
    }
}
